/**
 * Takeover templates — full-bleed artist photo (no diamond pattern) with the
 * branding panels on top. Derived from Figma "Etape Takeover IG" (538:86),
 * "Etape Takeover SC" (534:58) and "Etape Takeover IG Story" (2039:92).
 *
 * The panel/logo/name-container background toggles between navy and black
 * (handy when a supplied photo/logo is transparent), so panels here store
 * geometry only — the fill colour comes from the composer's toggle.
 */
import { COLORS, NAME_FONT_FAMILY } from './templates';
import { type Rect } from './photoSlot';

export type TakeoverFill = 'navy' | 'black';
export const TAKEOVER_FILL: Record<TakeoverFill, string> = { navy: COLORS.navy, black: '#000000' };
export type TakeoverFormat = 'ig-post' | 'sc-post' | 'ig-story';

export interface TakeoverText {
  box: Rect;
  fontSize: number;
  fontFamily: string;
  weight: number;
  align: 'left' | 'center' | 'right';
  connector?: { x: number; y: number; length: number };
}

export interface TakeoverTemplate {
  id: string;
  label: string;
  format: TakeoverFormat;
  audience: 'artist' | 'internal';
  canvas: { width: number; height: number };
  /** Full-bleed photo covers the whole canvas. */
  photoFrame: Rect;
  /** Decorative orange frame drawn on top of the photo. */
  border: { rect: Rect; width: number };
  logoRect: Rect;
  name: TakeoverText;
  /** "TAKEOVER" label (posts). */
  takeoverLabel?: TakeoverText & { text: string };
  /** Date + time (story). */
  date?: TakeoverText;
  time?: TakeoverText;
}

const t = (
  box: Rect,
  fontSize: number,
  fontFamily = NAME_FONT_FAMILY,
  weight = 400,
  align: 'left' | 'center' | 'right' = 'center',
  connector?: { x: number; y: number; length: number },
): TakeoverText => ({ box, fontSize, fontFamily, weight, align, connector });

const igPost: TakeoverTemplate = {
  id: 'takeover-ig',
  label: 'Instagram (4:5)',
  format: 'ig-post',
  audience: 'internal',
  canvas: { width: 1080, height: 1440 },
  photoFrame: { x: 0, y: 0, width: 1080, height: 1440 },
  border: { rect: { x: 54, y: 103, width: 972, height: 1078 }, width: 4 },
  logoRect: { x: 38, y: 1098, width: 333, height: 218 },
  name: t({ x: 652, y: 1252, width: 329, height: 64 }, 36, NAME_FONT_FAMILY, 400, 'center', {
    x: 817,
    y: 1181,
    length: 74,
  }),
  takeoverLabel: { ...t({ x: 24, y: 76, width: 223, height: 73 }, 24), text: 'TAKEOVER' },
};

const scPost: TakeoverTemplate = {
  id: 'takeover-sc',
  label: 'SoundCloud (square)',
  format: 'sc-post',
  audience: 'internal',
  canvas: { width: 1350, height: 1350 },
  photoFrame: { x: 0, y: 0, width: 1350, height: 1350 },
  border: { rect: { x: 137, y: 62, width: 1076, height: 1076 }, width: 4 },
  logoRect: { x: 43, y: 1057, width: 333, height: 218 },
  name: t({ x: 889, y: 1180, width: 289, height: 60 }, 32, NAME_FONT_FAMILY, 400, 'center', {
    x: 1036,
    y: 1135,
    length: 47,
  }),
  takeoverLabel: { ...t({ x: 94, y: 29, width: 199, height: 66 }, 24), text: 'TAKEOVER' },
};

const igStory: TakeoverTemplate = {
  id: 'takeover-ig-story',
  label: 'Instagram Story',
  format: 'ig-story',
  audience: 'internal',
  canvas: { width: 1080, height: 1920 },
  photoFrame: { x: 0, y: 0, width: 1080, height: 1920 },
  border: { rect: { x: 54, y: 215, width: 972, height: 1396 }, width: 4 },
  logoRect: { x: 35, y: 1543, width: 270, height: 230 },
  name: t({ x: 40, y: 182, width: 329, height: 66 }, 32),
  date: t({ x: 704, y: 1662, width: 329, height: 47 }, 32, NAME_FONT_FAMILY, 400, 'center', {
    x: 946,
    y: 1610,
    length: 52,
  }),
  time: t({ x: 704, y: 1709, width: 329, height: 42 }, 24, NAME_FONT_FAMILY, 400),
};

export const TAKEOVER_TEMPLATES: TakeoverTemplate[] = [igPost, scPost, igStory];

export function getTakeoverTemplate(id: string): TakeoverTemplate | undefined {
  return TAKEOVER_TEMPLATES.find((t) => t.id === id);
}
